import {
  ChakraBox,
  EmptySectionCard,
  GroupSectionCard,
} from '@equip.health/ui';
import { useState } from 'react';
import {
  formatGroupSectionDate,
  formatGroupSectionDateRange,
  formatGroupSectionRecurrence,
  formatGroupSectionTimeSlot,
  formatHosts,
  getFormattedDurationWeeks,
} from '~/lib/util/groups.utils';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';

interface GroupSectionsListProps {
  id: string;
  groupSections: SectionData[];
  selectedTimezone?: string;
  getKebabOptions?: (groupSection: SectionData) => any[];
  height: string;
  onCardClick?: (groupSection: SectionData) => void;
  selectedSection?: SectionData;
}

const GroupSectionsList = ({
  id,
  groupSections,
  selectedTimezone,
  getKebabOptions,
  height,
  onCardClick,
  selectedSection,
}: GroupSectionsListProps) => {
  const [internalSelectedSectionId, setInternalSelectedSectionId] =
    useState<string>();

  const getSelectedDateRange = ({
    startDate,
    endDate,
    frequency,
  }: {
    startDate: string;
    endDate: string;
    frequency: SectionFrequencyCode;
  }) => {
    if (frequency === 'DOES_NOT_REPEAT') {
      return formatGroupSectionDate({
        date: startDate,
        timeZone: selectedTimezone,
      });
    }
    return formatGroupSectionDateRange({
      start: startDate,
      end: endDate,
      timeZone: selectedTimezone,
    });
  };

  return (
    <>
      {groupSections.length === 0 ? (
        <ChakraBox marginTop="6px">
          <EmptySectionCard
            caption="There are no sections associated with this group."
            id={`${id}__empty-section-card`}
          />
        </ChakraBox>
      ) : (
        <ChakraBox marginTop="4px" height={height} overflow="auto">
          {groupSections.map((item) => {
            const {
              id,
              startDate,
              sessionCode,
              endDate,
              duration,
              frequency,
              noOfRegistrants,
              hosts,
            } = item;
            return (
              <ChakraBox
                key={id}
                id={`${id}__group-section-card`}
                marginBottom="12px"
              >
                <GroupSectionCard
                  key={id}
                  id={`${id}__group-section-card-${id}`}
                  duration={getFormattedDurationWeeks(sessionCode)}
                  isSelected={
                    selectedSection?.id === id ||
                    internalSelectedSectionId === id
                  }
                  kebabItems={getKebabOptions?.(item)}
                  timeSlot={formatGroupSectionTimeSlot({
                    start: startDate,
                    duration,
                    timeZone: selectedTimezone,
                  })}
                  recurrence={formatGroupSectionRecurrence({
                    start: startDate,
                    frequency,
                  })}
                  sectionDateRange={getSelectedDateRange({
                    startDate,
                    endDate,
                    frequency,
                  })}
                  registeredUsersCount={noOfRegistrants}
                  hosts={formatHosts(hosts)}
                  onCardClick={() => {
                    if (onCardClick) {
                      onCardClick(item);
                      setInternalSelectedSectionId(id);
                    }
                  }}
                  tooltipWrapper={({ children, providerExternalId }) => (
                    <ProviderAvatarTooltipWrapper
                      actionTime={startDate}
                      isRoleAtAction
                      placement="left"
                      providerExternalId={providerExternalId}
                    >
                      {children}
                    </ProviderAvatarTooltipWrapper>
                  )}
                />
              </ChakraBox>
            );
          })}
        </ChakraBox>
      )}
    </>
  );
};

export default GroupSectionsList;
